import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Typography, GlobalStyles } from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, GeoJSON, Popup, Tooltip, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { TILES, MAP_ATTRIBUTION, FitBounds, popupCss } from "./mapShared";
import { DRC_CENTER } from "../../config/drcAdmin";
// CRA only parses ".json" imports into objects — a ".geojson" extension
// resolves to the built asset's URL string instead, so it must be fetched
// and parsed at runtime (same pattern GeoDashboardMap.tsx uses for its
// world-boundary GeoJSON) rather than imported and used directly.
import drcBoundaryUrl from "../../data/drc.geojson";

/* ═══════════════════════════════════════════════════════════════════
   DRC entry points — Air / Sea / Land, sourced from the real `entry_points`
   table (id, code, title, type, district, region, latitude, longitude) via
   GET /reports/entry-points, joined server-side against traveler_screenings
   counts. Every marker here has a real, authoritative coordinate — no
   guessing/fallback logic (that used to live here before the backend join
   existed; see RELEASE_NOTES.md history if resurrecting the old approach
   is ever needed as reference).
══════════════════════════════════════════════════════════════════ */
export type PortMapColors = {
  air: string;
  sea: string;
  land: string;
  tooltipBg: string;
  tooltipText: string;
  tooltipTextMuted: string;
};

// Mirrors DashboardPage.tsx tokens: C.blue, C.teal, C.amber, C.headerBg, C.onDark, C.onDarkMuted.
const DEFAULT_COLORS: PortMapColors = {
  air: "#2563eb",
  sea: "#0d9488",
  land: "#d97706",
  tooltipBg: "#0f1f35",
  tooltipText: "rgba(255,255,255,0.90)",
  tooltipTextMuted: "rgba(255,255,255,0.45)",
};

const F_MONO = "'JetBrains Mono', 'Fira Code', 'Courier New', monospace";
const F_SANS = "'Inter', 'Helvetica Neue', sans-serif";
// Must stay in sync with DashboardPage.tsx S.lg
const SHADOW_LG = "0 10px 36px rgba(15,31,53,0.13)";
const BOUNDARY_COLOR = "#0f1f35"; // distinct from air/sea/land legend colors — reads as map chrome, not a 4th category

// Legend chrome is deliberately always light (independent of the tooltipBg/mode
// props, which stay dark for the click-popup) to match the reference bubble-map
// style — a light card floating over the map, not a dark HUD panel.
const LEGEND_BG = "rgba(255,255,255,0.96)";
const LEGEND_BORDER = "rgba(15,31,53,0.08)";
const LEGEND_HEADING = "#0f1f35";
const LEGEND_TEXT = "#475569";

const numFmt = (v: number | undefined | null) => (v ?? 0).toLocaleString();

// Area-true proportional symbols (radius ∝ sqrt(value / max)), sized for the
// DRC overview fit (~zoom 5–6) so neighboring ports stay separable. Radius
// grows gently with zoom so markers stay readable when the user zooms in.
const getBubbleRadius = (screened: number, maxScreened: number, zoom: number) => {
  const ratio = Math.sqrt(Math.max(screened, 1) / Math.max(maxScreened, 1));
  const base = 4 + ratio * 8; // ~4–12 px at reference zoom
  const zoomScale = Math.pow(1.18, zoom - 5.5);
  return Math.max(3.5, Math.min(20, base * zoomScale));
};

/** Keeps bubble pixel size in sync with the current map zoom. */
const ZoomSync: React.FC<{ onZoom: (z: number) => void }> = ({ onZoom }) => {
  const map = useMapEvents({
    zoomend: (e) => onZoom(e.target.getZoom()),
    moveend: (e) => onZoom(e.target.getZoom()),
  });
  useEffect(() => {
    onZoom(map.getZoom());
  }, [map, onZoom]);
  return null;
};

type EntryType = "air" | "sea" | "land";

// entry_points.type is authoritative — one of these four exact strings.
const RAW_TYPE_TO_KEY: Record<string, EntryType> = {
  "Air-Port": "air",
  "Sea-Port": "sea",
  "Land-Port": "land",
  "Railway-Port": "land",
};

const TYPE_LABEL: Record<EntryType, string> = { air: "Airport", sea: "Seaport", land: "Land / Rail" };
// Kept for the click popup's small icon-prefixed type label — not used on the
// ambient bubble markers or legend, which are plain colored dots (see below).
const TYPE_EMOJI: Record<EntryType, string> = { air: "✈️", sea: "🚢", land: "🚧" };

const BUBBLE_LABEL_CSS = `
  .leaflet-tooltip.port-label-tooltip {
    background: transparent; border: none; box-shadow: none; padding: 0;
    font: 700 10px/1.2 'Inter', 'Helvetica Neue', sans-serif; color: #0f1f35;
    text-shadow: -1px 0 2px #fff, 1px 0 2px #fff, 0 -1px 2px #fff, 0 1px 2px #fff;
    white-space: nowrap; opacity: 1;
  }
  .leaflet-tooltip.port-label-tooltip::before { display: none; }
`;

// Single translucent color for every bubble (markers + legend) — type is
// conveyed by the on-map label and popup, not by color, per request.
const BUBBLE_COLOR = "#2563eb";

// Shape returned by GET /reports/entry-points (see backend/app/api/reports.py get_entry_points).
export type PortPoint = {
  code: string;
  title: string;
  type: string;
  district?: string;
  region?: string;
  latitude: number;
  longitude: number;
  screened?: number;
  cleared?: number;
  high_risk?: number;
  suspected?: number;
};

type PortMarker = {
  code: string;
  title: string;
  coords: { lat: number; lng: number };
  type: EntryType;
  district?: string;
  region?: string;
  screened: number;
  cleared: number;
  highRisk: number;
  suspected: number;
};

type Props = {
  ports?: PortPoint[];
  colors?: Partial<PortMapColors>;
  mode?: "light" | "dark";
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const PortEntryMap: React.FC<Props> = ({ ports = [], colors: colorsProp, mode = "light" }) => {
  const colors = useMemo<PortMapColors>(() => ({ ...DEFAULT_COLORS, ...colorsProp }), [colorsProp]);
  const typeColor: Record<EntryType, string> = useMemo(
    () => ({ air: colors.air, sea: colors.sea, land: colors.land }),
    [colors]
  );

  const markers = useMemo<PortMarker[]>(() =>
    ports
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => ({
        code: p.code,
        title: p.title || p.code,
        coords: { lat: p.latitude, lng: p.longitude },
        type: RAW_TYPE_TO_KEY[p.type] || "air",
        district: p.district,
        region: p.region,
        screened: p.screened || 0,
        cleared: p.cleared || 0,
        highRisk: p.high_risk || 0,
        suspected: p.suspected || 0,
      })),
    [ports]);

  const maxScreened = useMemo(
    () => markers.reduce((m, p) => Math.max(m, p.screened), 1),
    [markers]
  );

  const [zoom, setZoom] = useState(5);
  const onZoom = useCallback((z: number) => setZoom(z), []);

  const [drcBoundary, setDrcBoundary] = useState<any>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(drcBoundaryUrl)
      .then((r) => r.json())
      .then((geo) => { if (!cancelled) setDrcBoundary(geo); })
      .catch(() => {
        // Graceful degradation — markers/tiles still render without the boundary overlay
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", borderRadius: "16px", overflow: "hidden" }}>
      <GlobalStyles styles={`${BUBBLE_LABEL_CSS}${popupCss(colors.tooltipBg, SHADOW_LG)}`} />
      <MapContainer center={DRC_CENTER} zoom={5} minZoom={3} scrollWheelZoom={false} attributionControl={false} style={{ width: "100%", height: "100%" }}>
        <ZoomSync onZoom={onZoom} />
        <TileLayer key={mode} url={TILES[mode].url} attribution={MAP_ATTRIBUTION} subdomains={TILES[mode].subdomains} maxZoom={19} />
        {drcBoundary && (
          <GeoJSON
            data={drcBoundary}
            style={{ color: BOUNDARY_COLOR, weight: 2.5, opacity: 0.85, fill: false }}
            interactive={false}
          />
        )}
        <FitBounds
          positions={markers.map((m) => [m.coords.lat, m.coords.lng] as [number, number])}
          positionsKey={markers.map((m) => m.code).join("|")}
          padding={[50, 50]}
          maxZoom={8}
        />

        {markers.map((m) => {
          const radius = getBubbleRadius(m.screened, maxScreened, zoom);
          return (
          <CircleMarker
            key={m.code}
            center={[m.coords.lat, m.coords.lng]}
            radius={radius}
            pathOptions={{
              color: "#ffffff",
              weight: 1.5,
              fillColor: BUBBLE_COLOR,
              fillOpacity: 0.5,
              opacity: 0.95,
            }}
          >
            <Tooltip permanent direction="right" offset={[radius + 4, 0]} className="port-label-tooltip">
              {m.title}
            </Tooltip>
            <Popup className="geo-popup">
              <Box sx={{ minWidth: 180, fontFamily: F_MONO }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: F_MONO, color: colors.tooltipText, mb: 0.4 }}>
                  {m.title}
                </Typography>
                <Typography sx={{ fontSize: 10, fontFamily: F_MONO, fontWeight: 700, color: typeColor[m.type], mb: 0.4 }}>
                  {TYPE_EMOJI[m.type]} {TYPE_LABEL[m.type]}
                </Typography>
                {(m.district || m.region) && (
                  <Typography sx={{ fontSize: 10, fontFamily: F_MONO, color: colors.tooltipTextMuted, mb: 0.8 }}>
                    {[m.district, m.region].filter(Boolean).join(", ")}
                  </Typography>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.3 }}>
                  <Typography sx={{ fontSize: 10, fontFamily: F_MONO, color: colors.tooltipTextMuted }}>Screened</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: F_MONO, color: colors.tooltipText }}>
                    {numFmt(m.screened)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.3 }}>
                  <Typography sx={{ fontSize: 10, fontFamily: F_MONO, color: colors.tooltipTextMuted }}>Cleared</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: F_MONO, color: colors.tooltipText }}>
                    {numFmt(m.cleared)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.3 }}>
                  <Typography sx={{ fontSize: 10, fontFamily: F_MONO, color: colors.tooltipTextMuted }}>High-Risk</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: F_MONO, color: colors.tooltipText }}>
                    {numFmt(m.highRisk)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontSize: 10, fontFamily: F_MONO, color: colors.tooltipTextMuted }}>Suspected</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: F_MONO, color: colors.tooltipText }}>
                    {numFmt(m.suspected)}
                  </Typography>
                </Box>
              </Box>
            </Popup>
          </CircleMarker>
          );
        })}
      </MapContainer>

      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          left: 20,
          zIndex: 1000,
          background: LEGEND_BG,
          backdropFilter: "blur(8px)",
          border: `1px solid ${LEGEND_BORDER}`,
          borderRadius: "12px",
          p: 1.5,
          minWidth: 160,
          boxShadow: SHADOW_LG,
        }}
      >
        <Typography
          sx={{
            fontSize: 10, fontWeight: 700, fontFamily: F_SANS, color: LEGEND_HEADING,
            mb: 1, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.6px",
          }}
        >
          Screening Volume
        </Typography>
        {([
          { label: "Higher Volume", size: 12 },
          { label: "Lower Volume", size: 6 },
        ]).map((v) => (
          <Box key={v.label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
            <Box sx={{ width: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Box sx={{ width: v.size, height: v.size, borderRadius: "50%", background: `${BUBBLE_COLOR}66`, border: `1.5px solid ${BUBBLE_COLOR}cc` }} />
            </Box>
            <Typography sx={{ fontSize: 11, fontFamily: F_SANS, color: LEGEND_TEXT }}>{v.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PortEntryMap;
